/**
 * 模拟登录模块
 *
 * 跨域账户传递说明：
 * 平台（web.senoiot.com 等）与大屏不在同一域名，localStorage 按域名隔离无法直接共享。
 * 因此采用"URL 一次性传参"方案：
 *   1. 平台跳转大屏时，把 username 和密码的 SHA-512 密文放入 URL 查询参数；
 *   2. 大屏启动时读取该参数，写入自己域名下的本地缓存（saber-dashboard-login）；
 *   3. 随后立即清理 URL 参数，后续进入大屏完全从本地缓存读取，不再依赖路径。
 *
 * 安全说明：
 * - URL 中不传明文密码，只传 SHA-512 密文（与登录接口使用的密文一致）；
 * - 参数读取后立即从地址栏移除，避免长期残留在浏览器历史中；
 * - 本地缓存中保存的密码同样为 SHA-512 密文，不存明文。
 */
import CryptoJS from 'crypto-js'
import service from './request'

// 走 devServer/nginx 的 /api 代理（见 vue.config.js），避免跨域直连 web.senoiot.com 被浏览器拦截
const LOGIN_URL = '/api/blade-auth/oauth/token'

// 本地缓存中账户信息的 key（存储 { username, password(密文) }）
const ACCOUNT_KEY = 'saber-dashboard-login'

const DEFAULT_USERNAME = 'admin'
const DEFAULT_PASSWORD = 'd88acc744ebb3fd647433df792cdd6db2b8e957e6f8f7a4b0ae98c3e010fc74d76df6ccd655ffd5c0fc71802fbb205b38c88c1803d0dc80281b61d2f28f48489d5cce393be03ff8b4c650e9abf903a516887f84ded88a306d25ae5f8967281303427c4e9252f4e585221dee725add603'

/**
 * SHA-512 加密密码
 */
function encryptPassword(password) {
  return CryptoJS.SHA512(password).toString()
}

/**
 * 判断是否为 SHA-512 加密后的密文（128 位十六进制）
 */
function isEncrypted(password) {
  return typeof password === 'string' && password.length === 128 && /^[a-f0-9]+$/i.test(password)
}

/**
 * 从 URL 一次性读取平台跳转时传入的账户信息
 * 密码统一转为 SHA-512 哈希（兼容平台传明文或哈希两种情况）
 * 注意：该哈希仅用于与本地缓存比对校验，不作为登录密码直接使用
 */
function getUrlAccount() {
  try {
    const url = new URL(window.location.href)
    const username = url.searchParams.get('username')
    const password = url.searchParams.get('password')
    if (!username || !password) return null
    return {
      username,
      password: isEncrypted(password) ? password : encryptPassword(password)
    }
  } catch (e) {
    console.warn('[auth] 解析 URL 账户参数失败', e)
    return null
  }
}

/**
 * 校验平台传入的账户是否与本地缓存一致：
 * 传入的 SHA-512 哈希 === 本地存储的密码哈希，即视为校验通过
 * @param {object} urlAccount URL 传入账户 { username, password: 哈希 }
 * @param {object} cachedAccount 本地缓存账户 { username, password: 哈希 }
 * @returns {boolean}
 */
function verifyUrlAccount(urlAccount, cachedAccount) {
  if (!urlAccount || !cachedAccount) return false
  if (urlAccount.username !== cachedAccount.username) return false
  // 将两边统一转为 SHA-512 哈希后再比较
  const urlHash = isEncrypted(urlAccount.password) ? urlAccount.password : encryptPassword(urlAccount.password)
  const localHash = isEncrypted(cachedAccount.password) ? cachedAccount.password : encryptPassword(cachedAccount.password)
  return urlHash === localHash
}

/**
 * 从本地缓存读取账户信息（密码统一转为密文）
 */
function getCachedAccount() {
  const cached = localStorage.getItem(ACCOUNT_KEY)
  if (!cached) return null
  try {
    const account = JSON.parse(cached)
    if (account && account.username) {
      // 兼容历史明文缓存，登录前统一转成密文
      account.password = isEncrypted(account.password) ? account.password : encryptPassword(account.password)
      return account
    }
  } catch (e) {
    console.warn('[auth] 缓存账户信息解析失败', e)
  }
  return null
}

/**
 * 将账户信息缓存到本地（密码保存为 SHA-512 密文）
 */
function saveAccount(username, password) {
  localStorage.setItem(ACCOUNT_KEY, JSON.stringify({ username, password }))
}

/**
 * 清理地址栏中残留的 username/password/token 参数，避免敏感信息留在 URL
 */
function cleanUrlCredentials() {
  try {
    if (!window.location.search) return
    const url = new URL(window.location.href)
    if (url.searchParams.has('username') || url.searchParams.has('password') || url.searchParams.has('token')) {
      url.searchParams.delete('username')
      url.searchParams.delete('password')
      url.searchParams.delete('token')
      window.history.replaceState({}, document.title, url.toString())
    }
  } catch (e) {
    console.warn('[auth] 清理 URL 参数失败', e)
  }
}

/**
 * 从 URL 一次性读取平台跳转时传入的登录 token（优先使用，无需再调登录接口）
 */
function getUrlToken() {
  try {
    const url = new URL(window.location.href)
    return url.searchParams.get('token') || ''
  } catch (e) {
    return ''
  }
}

/**
 * 检查并执行登录
 * 1. 先从 URL 读取平台传入的一次性账户（有则缓存到本地并优先使用，平台切换账户时也能跟随）
 * 2. 否则若 localStorage 已有 saber-userInfo（token），直接返回
 * 3. 否则读取本地缓存的账户信息（saber-dashboard-login）登录
 * 4. 本地无缓存账户时使用默认 admin 账户
 * 5. 调用登录接口，成功后将 token 与账户信息存入 localStorage，最后清理 URL 敏感参数
 */
export async function checkLogin() {
  // 平台跳转时传入的一次性 token：直接使用，无需调登录接口（支持所有登录方式）
  const urlToken = getUrlToken()
  if (urlToken) {
    localStorage.setItem('saber-userInfo', JSON.stringify({ content: { access_token: urlToken } }))
    service.defaults.headers.common['blade-auth'] = 'bearer ' + urlToken
    console.log('[auth] 使用平台传入 token 登录')
    cleanUrlCredentials()
    return
  }

  // 从 URL 获取平台跳转时传入的一次性账户信息（仅作校验凭证）
  const urlAccount = getUrlAccount()

  // 已经登录过，且本次没有 URL 传入新账户，直接复用 token
  const stored = localStorage.getItem('saber-userInfo')
  if (stored && !urlAccount) {
    try {
      const info = JSON.parse(stored)
      if (info.content && info.content.access_token) {
        service.defaults.headers.common['blade-auth'] = 'bearer ' + info.content.access_token
        console.log('[auth] 已登录，跳过')
        return
      }
    } catch (e) {
      console.warn('[auth] saber-userInfo 解析失败，重新登录')
    }
  }

  // 确定登录账户：URL 校验通过 > 本地缓存 > 默认账户
  let username, password
  const cachedAccount = getCachedAccount()

  if (urlAccount) {
    if (verifyUrlAccount(urlAccount, cachedAccount)) {
      // 校验通过：使用本地缓存的密码哈希登录，不直接使用 URL 传入的值
      username = cachedAccount.username
      password = cachedAccount.password
      console.log('[auth] URL 传入账户校验通过，使用本地缓存登录:', username)
    } else {
      // 平台 URL 传入的账户是权威来源：刷新本地缓存后用其登录（避免旧缓存/换账户导致失败）
      username = urlAccount.username
      password = urlAccount.password
      saveAccount(username, password)
      console.log('[auth] 使用平台传入账户并刷新本地缓存:', username)
    }
  }

  if (!username) {
    if (cachedAccount) {
      username = cachedAccount.username
      password = cachedAccount.password
      console.log('[auth] 使用本地缓存账户登录:', username)
    } else {
      username = DEFAULT_USERNAME
      password = DEFAULT_PASSWORD
      console.log('[auth] 使用默认账户登录:', username)
    }
  }

  // 构建请求体
  const body = new URLSearchParams({
    tenantId: '000000',
    grant_type: 'password',
    type: 'account',
    username: username,
    scope: 'all',
    password: password
  }).toString()

  console.log('[auth] 正在登录...')

  try {
    const res = await fetch(LOGIN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Tenant-Id': '000000',
        'Authorization': 'Basic c2FiZXIzOnNhYmVyM19zZWNyZXQ='
      },
      body: body
    })

    const data = await res.json()

    if (data.access_token) {
      // 按 zt.js 中的格式存储：{ content: { access_token, refresh_token, ... } }
      localStorage.setItem('saber-userInfo', JSON.stringify({ content: data }))
      // 同步更新 axios 请求头的 blade-auth token
      service.defaults.headers.common['blade-auth'] = 'bearer ' + data.access_token
      console.log('[auth] 登录成功')
    } else {
      console.error('[auth] 登录失败:', data)
      // 清掉本地账户缓存与错误 token，避免刷新后一直被错误密码卡死
      localStorage.removeItem(ACCOUNT_KEY)
      localStorage.removeItem('saber-userInfo')
      throw new Error(data.msg || data.error_description || '登录失败')
    }
  } catch (err) {
    console.error('[auth] 登录请求异常:', err)
    throw err
  } finally {
    // 无论登录成功与否，都清理 URL 中的敏感参数
    cleanUrlCredentials()
  }
}

export default { checkLogin }
